import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  LifeBuoy, Search, Loader2, Send, Trash2, MessageSquare, AlertCircle, RefreshCw,
} from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

const STATUS_LABEL = {
  open: 'Ouvert',
  in_progress: 'En cours',
  resolved: 'Résolu',
  closed: 'Fermé',
};
const STATUS_BG = {
  open: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800',
  closed: 'bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600',
};
const PRIORITY_LABEL = {
  low: 'Faible',
  normal: 'Normale',
  high: 'Haute',
  urgent: 'Urgente',
};
const PRIORITY_BG = {
  low: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
  normal: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
  high: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
  urgent: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium border ${STATUS_BG[status] || ''}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}
function PriorityBadge({ priority }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium border ${PRIORITY_BG[priority] || ''}`}>
      {PRIORITY_LABEL[priority] || priority}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('fr-FR'); } catch (e) { return '—'; }
}

export default function AdminTickets() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // ticket detail object
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (search.trim()) params.search = search.trim();
      const r = await api.get('/admin/tickets', { params });
      setItems(Array.isArray(r.data) ? r.data : (r.data?.tickets || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du chargement des tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, priorityFilter]);

  const openDetail = async (ticketId) => {
    try {
      setDetailLoading(true);
      const r = await api.get(`/admin/tickets/${ticketId}`);
      setSelected(r.data);
      setReply('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement du ticket');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!selected?.id) return;
    const r = await api.get(`/admin/tickets/${selected.id}`);
    setSelected(r.data);
  };

  const updateField = async (key, value) => {
    if (!selected?.id) return;
    try {
      await api.patch(`/admin/tickets/${selected.id}`, { [key]: value });
      toast.success('Ticket mis à jour');
      await refreshDetail();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de mise à jour');
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) { toast.error('Le message ne peut pas être vide'); return; }
    if (!selected?.id) return;
    try {
      setSending(true);
      await api.post(`/admin/tickets/${selected.id}/reply`, { message: reply.trim() });
      toast.success('Réponse envoyée');
      setReply('');
      await refreshDetail();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete?.id) return;
    try {
      await api.delete(`/admin/tickets/${confirmDelete.id}`);
      toast.success('Ticket supprimé');
      if (selected?.id === confirmDelete.id) setSelected(null);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const visible = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.trim().toLowerCase();
    return items.filter((tk) =>
      [tk.subject, tk.client_name, tk.client_email].filter(Boolean).some((x) => x.toLowerCase().includes(s))
    );
  }, [items, search]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
            <LifeBuoy className="w-7 h-7 text-blue-700 dark:text-blue-400" /> {t('admin.ticketsTitle')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('admin.ticketsSubtitle')}
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="btn-tickets-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Rafraîchir
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 grid md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs text-slate-500 dark:text-slate-400 uppercase font-medium">Statut</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="select-tickets-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="open">Ouverts</SelectItem>
              <SelectItem value="in_progress">En cours</SelectItem>
              <SelectItem value="resolved">Résolus</SelectItem>
              <SelectItem value="closed">Fermés</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 dark:text-slate-400 uppercase font-medium">Priorité</Label>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger data-testid="select-tickets-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="low">Faible</SelectItem>
              <SelectItem value="normal">Normale</SelectItem>
              <SelectItem value="high">Haute</SelectItem>
              <SelectItem value="urgent">Urgente</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs text-slate-500 dark:text-slate-400 uppercase font-medium">Recherche</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Sujet, nom ou email client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              data-testid="input-tickets-search"
            />
            <Button onClick={load} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-tickets-search">
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Chargement...
          </div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400 text-sm">
            <AlertCircle className="w-6 h-6 mx-auto mb-2" /> Aucun ticket trouvé
          </div>
        ) : (
          <div className="divide-y dark:divide-slate-800">
            {visible.map((tk) => (
              <button
                key={tk.id}
                onClick={() => openDetail(tk.id)}
                className="w-full text-left p-4 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition flex flex-wrap items-center gap-4"
                data-testid={`row-ticket-${tk.id}`}
              >
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold dark:text-white">{tk.subject}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {tk.client_name || '—'} · {tk.client_email || '—'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={tk.status} />
                  <PriorityBadge priority={tk.priority} />
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 min-w-[100px] justify-end">
                  <MessageSquare className="w-3.5 h-3.5" /> {tk.response_count ?? (tk.responses?.length || 0)}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 min-w-[140px] text-right">
                  {fmtDate(tk.updated_at || tk.created_at)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-white">
              <LifeBuoy className="w-5 h-5 text-blue-700 dark:text-blue-400" />
              {selected?.subject || 'Ticket'}
            </DialogTitle>
            <DialogDescription>
              {selected?.client_name} · {selected?.client_email} · Créé le {fmtDate(selected?.created_at)}
            </DialogDescription>
          </DialogHeader>

          {detailLoading || !selected ? (
            <div className="py-8 text-center text-slate-500 dark:text-slate-400">
              <Loader2 className="w-5 h-5 mx-auto animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Controls row */}
              <div className="grid md:grid-cols-2 gap-3 bg-slate-50 dark:bg-slate-800/60 p-3 rounded-lg border dark:border-slate-700">
                <div>
                  <Label className="text-xs uppercase text-slate-500 dark:text-slate-400">Statut</Label>
                  <Select value={selected.status} onValueChange={(v) => updateField('status', v)}>
                    <SelectTrigger data-testid="select-detail-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Ouvert</SelectItem>
                      <SelectItem value="in_progress">En cours</SelectItem>
                      <SelectItem value="resolved">Résolu</SelectItem>
                      <SelectItem value="closed">Fermé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500 dark:text-slate-400">Priorité</Label>
                  <Select value={selected.priority} onValueChange={(v) => updateField('priority', v)}>
                    <SelectTrigger data-testid="select-detail-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Faible</SelectItem>
                      <SelectItem value="normal">Normale</SelectItem>
                      <SelectItem value="high">Haute</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Conversation */}
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {/* Original message */}
                <div className="flex">
                  <div className="max-w-[80%] bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 text-sm">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                      {selected.client_name || 'Client'} · {fmtDate(selected.created_at)}
                    </div>
                    <div className="whitespace-pre-line">{selected.message}</div>
                  </div>
                </div>
                {(selected.responses || []).map((r, idx) => {
                  const isAdmin = r.author_role === 'admin' || r.is_admin;
                  return (
                    <div key={r.id || idx} className={`flex ${isAdmin ? 'justify-end' : ''}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                        isAdmin
                          ? 'bg-blue-600 text-white rounded-tr-sm'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-sm'
                      }`}>
                        <div className={`text-xs font-semibold mb-1 ${isAdmin ? 'text-blue-100' : 'text-slate-500 dark:text-slate-400'}`}>
                          {isAdmin ? (r.author_name || 'Support BFI') : (selected.client_name || 'Client')} · {fmtDate(r.created_at)}
                        </div>
                        <div className="whitespace-pre-line">{r.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Reply */}
              <div className="space-y-2 pt-2 border-t dark:border-slate-700">
                <Label className="dark:text-slate-200">Réponse</Label>
                <Textarea
                  rows={4}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Tapez votre réponse..."
                  data-testid="textarea-ticket-reply"
                />
                <div className="flex justify-between gap-2 flex-wrap">
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmDelete(selected)}
                    data-testid="btn-ticket-delete"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                  </Button>
                  <Button
                    onClick={sendReply}
                    disabled={sending || !reply.trim()}
                    className="bg-blue-700 hover:bg-blue-800"
                    data-testid="btn-ticket-send-reply"
                  >
                    {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                    Envoyer
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dark:text-white">Supprimer ce ticket ?</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Le ticket <strong>{confirmDelete?.subject}</strong> et toutes ses réponses seront supprimés.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="btn-ticket-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1" /> Confirmer la suppression
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
